
class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector("form");
    this.button = this.form.querySelector('button[name="add"]');
    this.form.addEventListener("submit", this.onSubmit.bind(this));
    this.formData = new FormData(this.form);
  }

  async onSubmit(event) {
    event.preventDefault();

    const freeGifts = document.querySelector("free-gifts");
    if (freeGifts && !freeGifts.areSelectionsComplete()) {
      freeGifts.focusIncomplete();
      freeGifts.syncAtcButtons();
      return;
    }

    this.formData = new FormData(this.form);

    this.addQtyBreaks();
    this.addUpsells();
    this.addFreeGifts();

    this.button.classList.add("loading");
    this.button.disabled = true;
    this.button.setAttribute("aria-busy", "true");

    // const startTime = performance.now();
    await document.querySelector("cart-component").add(this.formData);
    // const durationMs = performance.now() - startTime;
    // console.log(`[ProductForm] cart add completed in ${durationMs.toFixed(2)}ms`);

    this.button.classList.remove("loading");
    this.button.disabled = false;
    this.button.setAttribute("aria-busy", "false");
    freeGifts?.syncAtcButtons?.();
  }

  addQtyBreaks() {
    let variantIds = this.querySelector('input[name*="qty-breaks"]:checked')?.value;
    if (!variantIds) return;

    const properties = {};
    for (const [key, value] of this.formData.entries()) {
      if (key.startsWith("properties[")) {
        const cleanKey = key.replace("properties[", "").replace("]", "");
        properties[cleanKey] = value;
      }
    }

    Object.keys(properties).forEach((p) => {
      this.formData.delete(`properties[${p}]`);
    });


    this.formData.delete("id");
    this.formData.delete("quantity");

    variantIds = variantIds.split(",");

    variantIds.forEach((variant, index) => {
      this.formData.append(`items[qty-breaks-${index}][id]`, variant);
      this.formData.append(`items[qty-breaks-${index}][quantity]`, 1);

      Object.keys(properties).forEach((p) => {
        this.formData.append(`items[qty-breaks-${index}][properties][${p}]`, properties[p]);
      });
    });
  }

  addUpsells() {
    const upsells = Array.from(this.form.querySelectorAll('[name*="upsell"]')).reduce((acc, input) => {
      if (input.checked) {
        acc.push({
          id: Number(input.value),
          quantity: Number(input.dataset.quantity || 1),
        });
      }
      return acc;
    }, []);

    upsells.forEach((item, index) => {
      this.formData.append(`items[upsells-${index}][id]`, item.id);
      this.formData.append(`items[upsells-${index}][quantity]`, item.quantity);
    });
  }

  addFreeGifts() {
    document.querySelector("free-gifts")?.applyToFormData?.(this.formData);
  }
}
customElements.define("product-form", ProductForm);

class ProductOptions extends HTMLElement {
  constructor() {
    super();

    this.form = this.closest("form");
    this.variantsData = this.getVariantsData();
    this.productOptions = this.querySelectorAll('[name*="option"]');
    this.optionsCount = new Set(Array.from(this.productOptions).map((elem) => elem.name)).size;
    this.sectionId = this.closest(".shopify-section")?.id.replace("shopify-section-", "") || null;
    this.variantFetchController = null;

    this.productOptions.forEach((option) => option.addEventListener("change", this.onChange.bind(this)));

    this.handleOptionsAvailability();
    this.updateOptionValuePrices();
  }

  getVariantsData() {
    const script = this.querySelector("[data-product-variants-json]");
    if (!script) return null;
    return JSON.parse(script.textContent);
  }

  async onChange() {
    const selectedOptions = Array.from(this.productOptions)
      .filter((elem) => (elem.type === "radio" && elem.checked) || elem.type === "select-one")
      .map((elem) => elem.value);

    const selectedVariant = this.variantsData.find((variant) => JSON.stringify(variant.options) === JSON.stringify(selectedOptions));
    // console.log("selectedVariant", selectedVariant);

    this.adjustButtons(selectedVariant);

    if (selectedVariant) {
      this.form.querySelector('[name="id"]').value = selectedVariant.id;

      if (this.closest('body[data-page-type="product"]')) {
        const url = new URL(window.location);
        url.searchParams.set("variant", selectedVariant.id);
        window.history.replaceState({}, "", url);
      }

      const colorSwatchesSelectedValue = this.form.querySelector(".color-swatches-selected-value");
      if (colorSwatchesSelectedValue) {
        colorSwatchesSelectedValue.textContent = this.form.querySelector('.color-swatches input:checked')?.value
      }

      this.handleOptionsAvailability();
      this.updateOptionValuePrices();
      this.adjustPricing(selectedVariant);

      window.dispatchEvent(
        new CustomEvent("variant:change", {
          detail: selectedVariant,
        }),
      );

      // Cancel any in-flight request so rapid variant switches don't stack up or race.
      this.variantFetchController?.abort();
      this.variantFetchController = new AbortController();

      // Fetch only the product section (Section Rendering API) instead of the whole page.
      const baseUrl = this.form.dataset.productUrl;
      const fetchUrl = this.sectionId
        ? `${baseUrl}?section_id=${encodeURIComponent(this.sectionId)}&variant=${selectedVariant.id}`
        : `${baseUrl}?variant=${selectedVariant.id}`;

      try {
        const response = await fetch(fetchUrl, { signal: this.variantFetchController.signal });
        const text = await response.text();
        const newDoc = new DOMParser().parseFromString(text, "text/html");

        window.dispatchEvent(
          new CustomEvent("variant:newDoc", {
            detail: newDoc,
          }),
        );
      } catch (error) {
        if (error.name !== "AbortError") throw error;
      }
    }
  }

  getCurrentSelectedOptions() {
    return Array.from(this.productOptions).reduce((selectedOptions, elem) => {
      const shouldUseElement = (elem.type === "radio" && elem.checked) || elem.type === "select-one";
      if (!shouldUseElement) return selectedOptions;

      const optionPosition = Number(elem.dataset.optionPosition);
      if (!optionPosition) return selectedOptions;

      selectedOptions[optionPosition - 1] = elem.value;
      return selectedOptions;
    }, []);
  }

  getVariantForOptionValue(optionPosition, optionValue, currentSelections) {
    if (!this.variantsData?.length || !optionPosition) return null;

    const normalizedSelections = Array.isArray(currentSelections) ? [...currentSelections] : [];
    normalizedSelections[optionPosition - 1] = optionValue;

    return (
      this.variantsData.find((variant) =>
        normalizedSelections.every((selectedValue, index) => !selectedValue || variant.options[index] === selectedValue),
      ) || null
    );
  }

  // updateOptionValuePrices() {
  //   if (!this.variantsData?.length || !window.Shopify?.formatMoney) return;

  //   const currentSelections = this.getCurrentSelectedOptions();

  //   this.querySelectorAll(".size-buttons input[data-option-position]").forEach((input) => {
  //     const optionPosition = Number(input.dataset.optionPosition);
  //     const optionValue = input.dataset.optionValue || input.value;
  //     const labelValueElement = this.querySelector(`label[for="${input.id}"] [data-option-value-label]`);
  //     if (!labelValueElement) return;

  //     const variantForValue = this.getVariantForOptionValue(optionPosition, optionValue, currentSelections);
  //     labelValueElement.textContent = variantForValue
  //       ? `${optionValue} (${window.Shopify.formatMoney(variantForValue.price)})`
  //       : optionValue;
  //   });

  //   this.querySelectorAll("select[data-option-position]").forEach((select) => {
  //     const optionPosition = Number(select.dataset.optionPosition);
  //     Array.from(select.options).forEach((optionElement) => {
  //       const optionValue = optionElement.dataset.optionValue || optionElement.value;
  //       const variantForValue = this.getVariantForOptionValue(optionPosition, optionValue, currentSelections);
  //       optionElement.textContent = variantForValue
  //         ? `${optionValue} (${window.Shopify.formatMoney(variantForValue.price)})`
  //         : optionValue;
  //     });
  //   });
  // }


  updateOptionValuePrices() {
  if (!this.variantsData?.length || !window.Shopify?.formatMoney) return;

  const PRICE_OPTION_HANDLES = ["material"]; // only these options show prices
  const currentSelections = this.getCurrentSelectedOptions();

  const getOptionHandleFromAttr = (value) => {
    if (!value) return "";
    const parts = value.split("-");
    if (parts.length < 3) return "";
    return parts.slice(1, -1).join("-"); // option-material-123456789 -> material
  };

  const getOptionValueMeta = (optionPosition, optionValues) => {
    const items = optionValues.map((optionValue) => {
      const variant = this.getVariantForOptionValue(optionPosition, optionValue, currentSelections);
      return {
        optionValue,
        variant,
        price: variant?.price ?? null,
      };
    });

    const uniquePrices = [...new Set(items.map((item) => item.price).filter((price) => price !== null))];
    const hasDifferentPrices = uniquePrices.length > 1;

    return { items, hasDifferentPrices };
  };

  const buildLabel = (optionValue, variant, hasDifferentPrices, isPriceOption) => {
    if (!isPriceOption || !hasDifferentPrices || !variant) return optionValue;
    return `${optionValue} (${window.Shopify.formatMoney(variant.price)})`;
  };

  // Button-style options
  this.querySelectorAll(".size-buttons").forEach((group) => {
    const inputs = Array.from(group.querySelectorAll("input[data-option-position]"));
    if (!inputs.length) return;

    const optionPosition = Number(inputs[0].dataset.optionPosition);
    const optionHandle = getOptionHandleFromAttr(inputs[0].name);
    const isPriceOption = PRICE_OPTION_HANDLES.includes(optionHandle);

    const optionValues = inputs.map((input) => input.dataset.optionValue || input.value);
    const { items, hasDifferentPrices } = getOptionValueMeta(optionPosition, optionValues);
    const byValue = new Map(items.map((item) => [item.optionValue, item.variant]));

    inputs.forEach((input) => {
      const optionValue = input.dataset.optionValue || input.value;
      const variant = byValue.get(optionValue) || null;
      const labelValueElement = this.querySelector(`label[for="${input.id}"] [data-option-value-label]`);
      if (!labelValueElement) return;

      labelValueElement.textContent = buildLabel(optionValue, variant, hasDifferentPrices, isPriceOption);
    });
  });

  // Select-style options
  this.querySelectorAll("select[data-option-position]").forEach((select) => {
    const optionPosition = Number(select.dataset.optionPosition);
    const optionHandle = getOptionHandleFromAttr(select.id);
    const isPriceOption = PRICE_OPTION_HANDLES.includes(optionHandle);

    const optionValues = Array.from(select.options)
      .map((optionElement) => optionElement.dataset.optionValue || optionElement.value)
      .filter(Boolean);
    const { items, hasDifferentPrices } = getOptionValueMeta(optionPosition, optionValues);
    const byValue = new Map(items.map((item) => [item.optionValue, item.variant]));

    Array.from(select.options).forEach((optionElement) => {
      // Placeholder options (value="") must keep their label — never overwrite.
      if (!optionElement.value || optionElement.hasAttribute("data-placeholder-option")) return;

      const optionValue = optionElement.dataset.optionValue || optionElement.value;
      const variant = byValue.get(optionValue) || null;

      optionElement.textContent = buildLabel(optionValue, variant, hasDifferentPrices, isPriceOption);
    });
  });
}



  adjustPricing(selectedVariant) {
    if (!selectedVariant) return;

    const priceContainer = this.form.querySelector(".price");
    if (!priceContainer) return;

    priceContainer.setAttribute("data-has-price-compare", selectedVariant.compare_at_price > selectedVariant.price);
    priceContainer.setAttribute("data-sold-out", !selectedVariant.available);
    priceContainer.setAttribute("data-has-unit-price", selectedVariant.unit_price > 0);

    priceContainer.querySelectorAll("[data-compare-price]").forEach((elem) => {
      elem.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price);
    });

    priceContainer.querySelectorAll("[data-price]").forEach((elem) => {
      elem.textContent = window.Shopify.formatMoney(selectedVariant.price);
    });

    priceContainer.querySelectorAll("[data-unit-price]").forEach((elem) => {
      elem.textContent = `(${window.Shopify.formatMoney(selectedVariant.unit_price)}/${selectedVariant.unit_price_measurement?.reference_unit})`;
    });

    priceContainer.querySelectorAll(".badge-sale").forEach((elem) => {
      const text = elem.dataset.text;
      const discountType = elem.dataset.discountType;
      let savings = "";
      if (discountType === "percentage") {
        savings = 100 - Math.round((selectedVariant.price / selectedVariant.compare_at_price) * 100) + "%";
      } else {
        savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price);
      }
      elem.textContent = text.replace("{{ value }}", savings);
    });
  }

  adjustButtons(selectedVariant) {
    const atcBtn = this.form.querySelector('button[name="add"]');
    const buyBtn = this.form.querySelector('button[name="buy"]');

    if (selectedVariant) {
      if (atcBtn) {
        atcBtn.textContent = selectedVariant.available ? atcBtn.dataset.textAdd : atcBtn.dataset.textSoldOut;
        atcBtn.disabled = !selectedVariant.available;
      }
      if (buyBtn) {
        buyBtn.disabled = !selectedVariant.available;
      }
    } else {
      // No matching variant: either the shopper hasn't chosen every option yet
      // (show "Select options") or the chosen combination doesn't exist
      // (show "Unavailable"). Either way the buttons must stay disabled.
      const allSelected = this.areAllOptionsSelected();
      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.textContent = allSelected
          ? atcBtn.dataset.textUnavailable
          : atcBtn.dataset.textChoose || atcBtn.dataset.textUnavailable;
      }
      if (buyBtn) {
        buyBtn.disabled = true;
      }
    }

    window.ProductAtcGate?.refresh?.();
  }

  areAllOptionsSelected() {
    const selections = this.getCurrentSelectedOptions();
    for (let i = 0; i < this.optionsCount; i++) {
      if (!selections[i]) return false;
    }
    return true;
  }

  handleOptionsAvailability() {
    const selectedOption1 = this.form.querySelector('input[data-option-position="1"]:checked')?.value;
    const selectedOption2 = this.form.querySelector('input[data-option-position="2"]:checked')?.value;
    const selectedOption3 = this.form.querySelector('input[data-option-position="3"]:checked')?.value;

    this.form.querySelectorAll('input[data-option-position="1"]').forEach((input) => {
      const isAvailable = this.variantsData.some((variant) => {
        if (variant.option1 !== input.value) return false;
        if (selectedOption2 && variant.option2 !== selectedOption2) return false;
        if (selectedOption3 && variant.option3 !== selectedOption3) return false;
        return variant.available;
      });

      this.setOptionAvailability(input, isAvailable);
    });

    this.form.querySelectorAll('input[data-option-position="2"]').forEach((input) => {
      const isAvailable = this.variantsData.some((variant) => {
        if (variant.option2 !== input.value) return false;
        if (selectedOption1 && variant.option1 !== selectedOption1) return false;
        if (selectedOption3 && variant.option3 !== selectedOption3) return false;
        return variant.available;
      });

      this.setOptionAvailability(input, isAvailable);
    });

    this.form.querySelectorAll('input[data-option-position="3"]').forEach((input) => {
      const isAvailable = this.variantsData.some((variant) => {
        if (variant.option3 !== input.value) return false;
        if (selectedOption1 && variant.option1 !== selectedOption1) return false;
        if (selectedOption2 && variant.option2 !== selectedOption2) return false;
        return variant.available;
      });

      this.setOptionAvailability(input, isAvailable);
    });
  }

  setOptionAvailability(input, isAvailable) {
    if (isAvailable) {
      input.classList.remove("disabled");
      input.removeAttribute("aria-disabled");
    } else {
      input.classList.add("disabled");
      input.setAttribute("aria-disabled", "true");
    }
  }
}
customElements.define("product-options", ProductOptions);

class ProductBuyButton extends HTMLElement {
  constructor() {
    super();

    this.btn = this.querySelector('button[name="buy"]');
    this.form = this.closest("form");

    this.btn.addEventListener("click", this.onClick.bind(this));
  }

  onClick(event) {
    event.preventDefault();

    const freeGifts = document.querySelector("free-gifts");
    if (freeGifts && !freeGifts.areSelectionsComplete()) {
      freeGifts.focusIncomplete();
      freeGifts.syncAtcButtons();
      return;
    }

    this.btn.classList.add("loading");
    const variantId = this.form.querySelector('[name="id"]').value;
    const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1);

    let items = [
      { id: variantId, quantity: qty }
    ];

    const qtyBreaks = this.closest('.product-form').querySelector('product-qty-breaks');

    if (qtyBreaks) {
      const variantIds = qtyBreaks.querySelector('input[name*="qty-breaks"]:checked')?.value;
      if (variantIds) {
        items = [];
        variantIds.split(",").forEach((id) => {
          items.unshift({ id: id, quantity: 1 });
        });
      }
    }

    this.closest('.product-form')?.querySelectorAll('[name*="upsell"]').forEach((input) => {
      if (input.checked) {
        items.unshift({ id: input.value, quantity: input.dataset.quantity || 1 });
      }
    });

    if (freeGifts?.prependGiftItems) {
      items = freeGifts.prependGiftItems(items);
    }

    window.location.href = `${this.dataset.cartUrl}/${items.map(item => `${item.id}:${item.quantity}`).join(',')}`;

    setTimeout(() => {
      this.btn.classList.remove("loading");
    }, 3000);
  }
  
}
customElements.define("product-buy-button", ProductBuyButton);

class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.isVariantFiltered = this.dataset.variantGallery === "true";

    this.track = this.querySelector(".product-media-gallery-track");
    this.thumbsContainer = this.querySelector(".product-media-gallery-thumbs");
    this.controls = this.querySelectorAll(".product-media-gallery-control");
    this.pagination = this.querySelector(".product-media-gallery-pagination");

    this.wrapper = this.closest(".product-media-gallery-wrapper");
    this.dialog = this.wrapper?.querySelector("dialog");
    this.modalList = this.dialog?.querySelector(".product-media-gallery-modal-list");
    this.variantFragments = new Map();

    this.cacheMedia();

    this.handleControls();
    this.handleThumbs();
    this.handleThumbsLayout();
    this.handleTouch();
    this.handleKeyboard();
    this.handleSticky();
    this.handleModalGallery();
    this.handleVariantChange();

    this.go(Number(this.dataset.initialIndex) || 0);
    this.setAttribute("data-init", "");
  }

  cacheMedia() {
    this.slides = Array.from(this.track.querySelectorAll(".product-media-gallery-slide"));
    this.thumbs = this.thumbsContainer ? Array.from(this.thumbsContainer.querySelectorAll("button")) : [];
    this.modalItems = this.modalList ? Array.from(this.modalList.querySelectorAll(".product-media-gallery-modal-item")) : [];
    this.updateModalIndices();
  }

  updateModalIndices() {
    this.slides.forEach((slide, index) => {
      const link = slide.querySelector("a[data-open-dialog]");
      if (link) link.dataset.index = index;
    });
  }

  prev() {
    this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.update();
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this.update();
  }

  go(index) {
    if (this.dataset.dynamicSpeed === "true") {
      const distance = Math.abs(this.currentIndex - index);
      const base = Number(this.dataset.speed) || 300;
      const increment = 80;
      const duration = Math.min(base + distance * increment, 1000);
      this.track.style.transitionDuration = `${duration}ms`;
    }

    this.currentIndex = index;
    this.update();
  }

  update() {
    const offset = -this.currentIndex * 100;
    this.track.style.transform = `translateX(${offset}%)`;

    this.slides.forEach((slide, index) => {
      slide.classList.toggle("active", index === this.currentIndex);
      slide.setAttribute("aria-hidden", index !== this.currentIndex);
      slide.querySelector("a")?.setAttribute("tabindex", index === this.currentIndex ? "0" : "-1");
    });

    this.updatePagination();
    this.updateThumbs();
    this.updateThumbsScroll();
  }

  handleControls() {
    this.controls.forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.name === "previous") {
          this.prev();
        } else {
          this.next();
        }
      });
    });
  }

  updatePagination() {
    if (!this.pagination) return;
    this.pagination.textContent = `${this.currentIndex + 1}/${this.slides.length}`;
  }

  handleThumbs() {
    if (!this.thumbsContainer) return;
    this.thumbsContainer.addEventListener("click", (event) => {
      const button = event.target.closest("button");
      if (!button) return;
      const index = this.thumbs.indexOf(button);
      if (index >= 0) this.go(index);
    });
  }

  updateThumbs() {
    this.thumbs.forEach((thumb, index) => {
      thumb.classList.toggle("active", index === this.currentIndex);
      thumb.setAttribute("aria-selected", index === this.currentIndex);
    });
  }

  updateThumbsScroll() {
    const track = this.querySelector(".product-media-gallery-thumbs");
    if (!track) return;

    // Defer layout reads to the next frame so they don't force a synchronous
    // reflow interleaved with the style writes in update().
    requestAnimationFrame(() => {
      const activeThumb = this.querySelector(".product-media-gallery-thumbs button.active");
      if (!activeThumb) return;

      if (this.dataset.thumbsPosition === "left") {
        const top = activeThumb.offsetTop - activeThumb.clientHeight * 2;
        track.scrollTo({ top: top, behavior: "smooth" });
      } else {
        const left = activeThumb.offsetLeft - activeThumb.clientWidth * 2;
        track.scrollTo({ left: left, behavior: "smooth" });
      }
    });
  }

  handleThumbsLayout() {
    if (this.dataset.thumbsPosition !== "left") return;

    const inner = this.querySelector(".product-media-gallery-inner");
    const thumbs = this.querySelector(".product-media-gallery-thumbs");
    if (!inner || !thumbs) return;

    const sync = () => {
      requestAnimationFrame(() => {
        this.style.setProperty("--main-media-height", `${inner.clientHeight}px`);
      });
    };

    sync();

    if ("ResizeObserver" in window) {
      this.thumbsResizeObserver = new ResizeObserver(sync);
      this.thumbsResizeObserver.observe(inner);
    } else {
      window.addEventListener("resize", sync);
    }
  }

  handleTouch() {
    let startX = 0;
    let endX = 0;

    this.track.addEventListener("touchstart", (e) => {
      startX = e.touches[0].clientX;
    });

    this.track.addEventListener("touchmove", (e) => {
      endX = e.touches[0].clientX;
    });

    this.track.addEventListener("touchend", () => {
      if (!startX || !endX) return;
      const swipeDistance = startX - endX;

      if (swipeDistance > 10) {
        this.next();
      } else if (swipeDistance < -10) {
        this.prev();
      }
    });
  }

  handleKeyboard() {
    this.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") this.prev();
      else if (event.key === "ArrowRight") this.next();
    });
  }

  handleSticky() {
    if (window.matchMedia("(min-width: 1200px)").matches) {
      const headerGroup = document.querySelector("#header-group.sticky");
      const stickyHeader = document.querySelector("#header").dataset.stickyHeader;

      switch (stickyHeader) {
        case "none": {
          this.style.top = "20px";
          break;
        }
        case "scroll-up": {
          let oldScroll = window.scrollY;
          let stickyTicking = false;

          window.addEventListener("scroll", () => {
            if (stickyTicking) return;
            stickyTicking = true;
            requestAnimationFrame(() => {
              const newScroll = window.scrollY;
              if (newScroll > oldScroll) {
                if (newScroll > headerGroup.clientHeight) {
                  this.style.top = "20px";
                }
              } else if (newScroll < oldScroll) {
                this.style.top = `${headerGroup.clientHeight + 20}px`;
              }

              oldScroll = Math.max(window.scrollY, 0);
              stickyTicking = false;
            });
          }, { passive: true });
          break;
        }
        case "always": {
          this.style.top = `${headerGroup.clientHeight + 20}px`;
          break;
        }
      }
    }
  }

  handleModalGallery() {
    if (!this.dialog) return;

    window.addEventListener("dialog:open", (event) => {
      if (event.detail.dialog.id !== this.dialog.id) return;

      const index = Number(event.detail.triggerElement.dataset.index || 0);
      const item = this.modalItems[index];
      if (!item) return;

      const dialogHeight = this.dialog.clientHeight;
      const top = item.offsetTop + (item.clientHeight - dialogHeight) / 2;
      this.dialog.scrollTo({ top: top, behavior: "smooth" });

      this.dialog.querySelectorAll('video').forEach((video) => {
        video.play();
      });
    });

    window.addEventListener("dialog:close", (event) => {
      if (event.detail.dialog.id !== this.dialog.id) return;

      this.dialog.querySelectorAll('video').forEach((video) => {
        video.pause();
      });
    });

  }

  // handleVariantChange() {
  //   window.addEventListener(
  //     "variant:change",
  //     (event) => {
  //       const selectedVariant = event.detail;
  //       if (selectedVariant.featured_media) {
  //         this.go(selectedVariant.featured_media.position - 1);
  //       }
  //     },
  //     false,
  //   );
  // }


  handleVariantChange() {
    // Filtered gallery: swap the variant-specific media client-side (no fetch, no DOM rebuild).
    if (this.isVariantFiltered) {
      window.addEventListener("variant:change", (event) => {
        this.applyVariantMedia(event.detail);
      });
      return;
    }

    // Default gallery: just slide to the selected variant's featured media.
    window.addEventListener("variant:change", (event) => {
      const selectedVariant = event.detail;
      if (selectedVariant?.featured_media) {
        this.go(selectedVariant.featured_media.position - 1);
      }
    });
  }

  getVariantFragments(id) {
    if (this.variantFragments.has(id)) return this.variantFragments.get(id);

    const scope = this.wrapper || document;
    const fragments = {
      slides: scope.querySelector(`template[data-variant-slides="${id}"]`)?.content || null,
      thumbs: scope.querySelector(`template[data-variant-thumbs="${id}"]`)?.content || null,
      modal: scope.querySelector(`template[data-variant-modal="${id}"]`)?.content || null,
    };
    this.variantFragments.set(id, fragments);
    return fragments;
  }

  applyVariantMedia(variant) {
    if (!variant) return;

    const fragments = this.getVariantFragments(String(variant.id));

    // Remove the currently shown variant-specific media (common media stays put).
    this.track.querySelectorAll("[data-variant-specific]").forEach((node) => node.remove());
    this.thumbsContainer?.querySelectorAll("[data-variant-specific]").forEach((node) => node.remove());
    this.modalList?.querySelectorAll("[data-variant-specific]").forEach((node) => node.remove());

    // Insert this variant's media before the common media (cloned so templates stay reusable).
    if (fragments.slides) this.track.insertBefore(fragments.slides.cloneNode(true), this.track.firstChild);
    if (fragments.thumbs && this.thumbsContainer) {
      this.thumbsContainer.insertBefore(fragments.thumbs.cloneNode(true), this.thumbsContainer.firstChild);
    }
    if (fragments.modal && this.modalList) {
      this.modalList.insertBefore(fragments.modal.cloneNode(true), this.modalList.firstChild);
    }

    this.cacheMedia();
    this.go(0);
  }
}
customElements.define("product-media-gallery", ProductMediaGallery);

class ProductQtySwitcher extends HTMLElement {
  constructor() {
    super();

    this.qtyInput = this.querySelector("input");
    this.minusBtn = this.querySelector("[name='decrease']");
    this.plusBtn = this.querySelector("[name='increase']");

    this.qtyInput.addEventListener("change", () => this.onQtyInputChange());
    this.minusBtn.addEventListener("click", () => this.onMinusBtnClick());
    this.plusBtn.addEventListener("click", () => this.onPlusBtnClick());

    this.handleVariantChange();
  }

  onQtyInputChange() {
    this.validateButtons();
  }

  onMinusBtnClick() {
    this.qtyInput.value = Number(this.qtyInput.value) - 1;
    this.validateButtons();
    this.qtyInput.dispatchEvent(new CustomEvent("change"));
  }

  onPlusBtnClick() {
    this.qtyInput.value = Number(this.qtyInput.value) + 1;
    this.validateButtons();
    this.qtyInput.dispatchEvent(new CustomEvent("change"));
  }

  validateButtons() {
    if (this.qtyInput.getAttribute("min") && Number(this.qtyInput.value) <= Number(this.qtyInput.getAttribute("min"))) {
      this.minusBtn.disabled = true;
      this.qtyInput.value = this.qtyInput.getAttribute("min");
    } else {
      this.minusBtn.disabled = false;
    }
    if (this.qtyInput.getAttribute("max") && Number(this.qtyInput.value) >= Number(this.qtyInput.getAttribute("max"))) {
      this.plusBtn.disabled = true;
      this.qtyInput.value = this.qtyInput.getAttribute("max");
    } else {
      this.plusBtn.disabled = false;
    }
  }

  handleVariantChange() {
    window.addEventListener("variant:newDoc", async (event) => {
      const newDoc = event.detail;
      if (!newDoc) return;

      this.replaceWith(newDoc.querySelector(`product-qty-switcher`));
    });
  }

  onVariantChange(event) {
    if (!this.closest(".product-block-atc")) return;

    const newDoc = event.detail;
    const newQtyInput = newDoc.querySelector(".product-block-atc .qty-switcher input");

    this.qtyInput.setAttribute("max", newQtyInput.getAttribute("max"));
    this.validateButtons();
  }
}
customElements.define("product-qty-switcher", ProductQtySwitcher);

class InventoryBar extends HTMLElement {
  constructor() {
    super();

    this.progressBar = this.querySelector(".progress-bar");
    this.animate();

    window.addEventListener("variant:change", async () => {
      this.style.opacity = ".25";
    });

    window.addEventListener("variant:newDoc", async (event) => {
      const newDoc = event.detail;
      if (!newDoc) return;

      this.replaceWith(newDoc.querySelector(`inventory-bar`));
      this.animate();
    });
  }

  animate() {
    setTimeout(() => {
      this.progressBar.style.width = this.progressBar.dataset.width;
    }, 250);
  }

  handleVariantChange() {}
}
customElements.define("inventory-bar", InventoryBar);

class ProductQtyBreaks extends HTMLElement {
  constructor() {
    super();

    this.animateAtcButton();
    this.handleOptionChange();
    this.updateOnVariantChange();
    this.syncQuantityFromBreak();
  }

  syncQuantityFromBreak() {
    const form = this.closest("form");
    if (!form) return;

    const checked = this.querySelector('input[name*="qty-breaks"]:checked');
    if (!checked) return;

    const ids = checked.value.split(",").filter(Boolean);
    const qty = ids.length || 1;

    form.querySelectorAll('input[name="quantity"]').forEach((input) => {
      input.value = qty;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  animateAtcButton() {
    this.querySelectorAll("input[type='radio']").forEach((input) => {
      input.addEventListener("change", () => {
        this.syncQuantityFromBreak();

        const atcBtn = this.closest("form").querySelector('button[name="add"]');
        if (!atcBtn) return;

        setTimeout(() => {
          atcBtn.classList.add("animate");
        }, 250);

        setTimeout(() => {
          atcBtn.classList.remove("animate");
        }, 1500);
      });
    });
  }

  handleOptionChange() {
    this.querySelectorAll(".product-qty-break-variants select").forEach((select) => {
      select.addEventListener("change", () => {
        const group = select.closest(".product-qty-break");
        
        const discountType = group.dataset.discountType;
        const discountPercent = Number(group.dataset.discountPercent);
        const discountValue = Number(group.dataset.discountValue);

        let totalPrice = 0;
        let selectedVariants = "";

        group.querySelectorAll(".product-qty-break-variant").forEach((elem) => {
          const selectedOptions = Array.from(elem.querySelectorAll("select")).reduce((options, select) => [...options, select.value], []);
          // console.log("selectedOptions", selectedOptions);

          const variantData = this.closest("product-form").querySelector("product-options").variantsData;
          const selectedVariant = variantData.find((variant) => JSON.stringify(variant.options) === JSON.stringify(selectedOptions));

          if (selectedVariant) {
            totalPrice += selectedVariant.price;
            selectedVariants += `${selectedVariant.id},`;

            if (selectedVariant.available) {
              elem.querySelector(".product-qty-break-variant-sold-out").hidden = true;
            } else {
              elem.querySelector(".product-qty-break-variant-sold-out").hidden = false;
            }
          }
        });

        if (discountType === "percentage") {
          group.querySelector("[data-total").textContent = window.Shopify.formatMoney((totalPrice * (100 - discountPercent)) / 100);

          if (discountPercent > 0) {
            group.querySelector("[data-compare-total").textContent = window.Shopify.formatMoney(totalPrice);
          }

        } else {
          group.querySelector("[data-total").textContent = window.Shopify.formatMoney(totalPrice - discountValue);

          if (discountValue > 0) {
            group.querySelector("[data-compare-total").textContent = window.Shopify.formatMoney(totalPrice);
          }
        }

        group.querySelector("input[type='radio']").value = selectedVariants.slice(0, -1);
        this.syncQuantityFromBreak();
      });
    });
  }

  updateOnVariantChange() {
    window.addEventListener("variant:change", async () => {
      this.style.opacity = ".25";
    });

    window.addEventListener("variant:newDoc", async (event) => {
      const newDoc = event.detail;
      if (!newDoc) return;

      this.replaceWith(newDoc.querySelector('product-qty-breaks'));
    });
  }
}
customElements.define("product-qty-breaks", ProductQtyBreaks);

window.ProductAtcGate = {
  hintTimer: null,
  lastClickAt: 0,

  isSelectionBlocked(btn) {
    if (!btn?.disabled) return false;

    const label = btn.textContent.trim();
    if (btn.dataset.textSoldOut && label === btn.dataset.textSoldOut.trim()) return false;
    if (btn.dataset.textUnavailable && label === btn.dataset.textUnavailable.trim()) return false;

    const freeGifts = document.querySelector("free-gifts");
    if (freeGifts && !freeGifts.areSelectionsComplete()) return true;

    const form =
      btn.closest("form") ||
      btn.closest("product-form")?.querySelector("form") ||
      document.querySelector("product-form form");
    const variantId = form?.querySelector('[name="id"]')?.value;
    if (!variantId) return true;

    if (btn.hasAttribute("data-free-gifts-incomplete")) return true;
    if (btn.dataset.textChoose && label === btn.dataset.textChoose.trim()) return true;

    return false;
  },

  refresh(root = document) {
    root.querySelectorAll("[data-atc-gate]").forEach((gate) => {
      const btn = gate.querySelector('button[name="add"], button[name="buy"]');
      const hit = gate.querySelector(".product-atc-gate__hit");
      if (!btn || !hit) return;

      const blocked = this.isSelectionBlocked(btn);
      gate.classList.toggle("is-blocked", blocked);
      hit.hidden = !blocked;

      if (!hit.dataset.bound) {
        hit.dataset.bound = "true";
        hit.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.onBlockedClick(gate, btn);
        });
      }
    });
  },

  onBlockedClick(gate, btn) {
    const now = Date.now();
    if (now - this.lastClickAt < 400) return;
    this.lastClickAt = now;

    const freeGifts = document.querySelector("free-gifts");
    const giftsIncomplete = freeGifts && !freeGifts.areSelectionsComplete();

    const form =
      btn.closest("form") ||
      btn.closest("product-form")?.querySelector("form") ||
      document.querySelector("product-form form");
    const variantId = form?.querySelector('[name="id"]')?.value;
    const productIncomplete = !variantId;

    let message = gate.dataset.hintProduct || btn.dataset.textChoose || "";
    let focusTarget = document.querySelector("product-options") || document.querySelector(".product-block[data-type='options']");

    if (giftsIncomplete && !productIncomplete) {
      message = gate.dataset.hintGifts || freeGifts.dataset.textChoose || message;
      focusTarget = freeGifts;
      freeGifts.focusIncomplete?.();
      freeGifts.classList.add("is-attention");
      setTimeout(() => freeGifts.classList.remove("is-attention"), 1600);
    } else if (productIncomplete) {
      focusTarget?.classList?.add("is-attention");
      setTimeout(() => focusTarget?.classList?.remove("is-attention"), 1600);
      focusTarget?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      const incompleteOption =
        focusTarget?.querySelector("select option[data-placeholder-option]:checked")?.closest(".select-wrapper") ||
        focusTarget?.querySelector("select:has(option[data-placeholder-option]:checked)") ||
        focusTarget?.querySelector("fieldset:not(:has(input:checked))") ||
        focusTarget;
      incompleteOption?.querySelector?.("select, input, button")?.focus?.();
    } else if (giftsIncomplete) {
      message = gate.dataset.hintGifts || freeGifts.dataset.textChoose || message;
      focusTarget = freeGifts;
      freeGifts.focusIncomplete?.();
      freeGifts.classList.add("is-attention");
      setTimeout(() => freeGifts.classList.remove("is-attention"), 1600);
    }

    this.showHint(gate, message);
  },

  getHintHost(gate) {
    return (
      gate.closest('.product-block[data-type="buy_buttons"]') ||
      gate.closest("sticky-atc") ||
      gate.closest("product-form") ||
      document.querySelector('.product-block[data-type="buy_buttons"]') ||
      gate.parentElement
    );
  },

  showHint(gate, message) {
    if (!message) return;

    const host = this.getHintHost(gate);
    if (!host) return;

    const existing = host.querySelector(":scope > .product-selection-hint");

    // One shared cue for ATC + Buy now. If already visible, only refresh the timer.
    if (existing) {
      existing.textContent = message;
      clearTimeout(this.hintTimer);
      this.hintTimer = setTimeout(() => existing.remove(), 2500);
      return;
    }

    document.querySelectorAll(".product-selection-hint").forEach((el) => el.remove());

    const hint = document.createElement("div");
    hint.className = "product-selection-hint";
    hint.setAttribute("role", "status");
    hint.setAttribute("aria-live", "polite");
    hint.textContent = message;
    host.appendChild(hint);

    clearTimeout(this.hintTimer);
    this.hintTimer = setTimeout(() => hint.remove(), 2500);
  },
};

document.addEventListener("DOMContentLoaded", () => window.ProductAtcGate.refresh());
if (document.readyState !== "loading") {
  window.ProductAtcGate.refresh();
}

class ProductReviewsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-reviews-track]");
    this.dots = Array.from(this.querySelectorAll("[data-reviews-dot]"));
    if (!this.track) return;

    this.onScroll = () => this.updateDots();
    this.onDotClick = (event) => {
      const btn = event.currentTarget;
      const index = Number(btn.dataset.index || 0);
      this.goTo(index);
    };

    this.dots.forEach((dot) => dot.addEventListener("click", this.onDotClick));
    this.track.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onScroll);
    this.updateDots();
  }

  disconnectedCallback() {
    this.dots?.forEach((dot) => dot.removeEventListener("click", this.onDotClick));
    this.track?.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onScroll);
  }

  getStep() {
    const card = this.track.querySelector(".product-reviews__card");
    if (!card) return 0;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 12;
    return card.offsetWidth + gap;
  }

  goTo(index) {
    const step = this.getStep();
    if (!step) return;
    this.track.scrollTo({ left: step * index, behavior: "smooth" });
  }

  updateDots() {
    if (!this.track || !this.dots.length) return;

    const step = this.getStep();
    if (!step) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const canScroll = maxScroll > 8;
    this.classList.toggle("is-scrollable", canScroll);

    const index = Math.round(this.track.scrollLeft / step);
    const active = Math.max(0, Math.min(index, this.dots.length - 1));

    this.dots.forEach((dot, i) => {
      const isActive = i === active;
      dot.classList.toggle("is-active", isActive);
      dot.setAttribute("aria-selected", isActive ? "true" : "false");
    });
  }
}

if (!customElements.get("product-reviews-carousel")) {
  customElements.define("product-reviews-carousel", ProductReviewsCarousel);
}

